import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { customGetAPI } from "services/utils/CustomAPI";
import * as Mui from "@mui/material";
import { useForm } from "react-hook-form";
import { showToast } from "services/utils/Status";
import { themes } from "services/constants";
import { setGlobalSearchValue } from "services/Redux/userToken";
import { CustomButton } from "components/CustomComponents/CustomButton";
import { useFetch } from "services/hooks/useFetch";
import { Info } from "@mui/icons-material";
import {
  formHeader,
  formBackGroundColor,
  formRightSideAlign,
  formKeyText,
  rowEndStyle,
  tooltipStyleProps,
} from "services/constants/Styles";
import { Loader } from "components/Loader";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { BackArrow } from "components/CustomComponents/BackArrow";
import sessionHandling from "services/utils/notificationUtils";
import { CustomAutoComplete } from "components/CustomComponents/CustomAutoComplete";

export const CreateUser = () => {
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    trigger,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      user_type: "User",
    },
  });
  const history = useHistory();
  const dispatch = useDispatch();
  const { domain, token } = useSelector((state) => state.tokenReducer);
  const { result: companyDetails } = useFetch(`${domain}get-active-company/`);
  const [loading, setLoading] = useState(false);
  const [buttonLoader, setButtonLoader] = useState(false);
  const handleKeyPress = (e) => {
    const charCode = e.charCode;
    if (charCode < 48 || charCode > 57) {
      e.preventDefault();
    }
  };

  const checkSpecialChar = (e) => {
    if (!/^[a-zA-Z0-9 ]*$/.test(e.key)) {
      e.preventDefault();
    }
  };



  const onSubmit = async (data) => {
    setButtonLoader(true);
    try {
      const response = await fetch(`${domain}normal_user_registration/`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `token ${token}`,
        },
        body: JSON.stringify({
          phone_number: data.phoneno,
          user_name: data.name,
          email: data.email,
          role: data?.user_type,
          company_id: data.company_id,
        }),
      });
      const res = await response.json();
      if (response.ok) {
        reset();
        showToast(
          "success",
          data?.user_type === "User"
            ? "User created successfully"
            : "Super admin created successfully"
        );
        setLoading(false);
        dispatch(setGlobalSearchValue(""));
        history.push("/admin/administration/user");
      } else if (response.status === 409) {
        sessionHandling();
      } else {
        throw new Error(res.error);
      }
    } catch (error) {
      showToast("error", error.message);
    } finally {
      setButtonLoader(false);
    }
  };

  // const handlePreventCopyPaste = (event) => {
  //   event.preventDefault();
  // };

  const handleCompanyName = (event, data) => {
    if (data) {
      setValue("company", data?.label);
      setValue("company_id", data?.id);
    } else {
      setValue("company", "");
      setValue("company_id", null);
    }
  };

  const handleUserType = (event) => {
    setValue("user_type", event.target.value);
  };

  return (
    <React.Fragment>
      <Mui.Grid container sx={{ paddingTop: 2 }}>
        <BackArrow
          actionFunction={() => history.push("/admin/administration/user")}
        />
        &nbsp;
        <Mui.Typography
          color={themes.headLine}
          sx={{
            paddingTop: 0.5,
            fontWeight: "bold",
            // fontFamily: "Popins",
            fontSize: 25,
            paddingLeft: { xs: 1, sm: 1 },
          }}
        >
          Create new user
        </Mui.Typography>
      </Mui.Grid>
      {loading ? (
        <Mui.Box>
          <Loader info="Loading..." />
        </Mui.Box>
      ) : (
        <>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Mui.Grid
              container
              sx={{
                paddingTop: "5%",
                paddingLeft: "25%",
                paddingRight: "25%",
                paddingBottom: "5%",
              }}
            >
              <Mui.Grid
                container
                sx={{
                  border: `2px solid ${themes.sidebarDivider}`,
                  borderRadius: "10px 10px 10px 10px",
                }}
              >
                <Mui.Grid
                  xs={12}
                  sx={{
                    backgroundColor: formBackGroundColor,
                    padding: {
                      xs: 0.5,
                      sm: 0.5,
                      md: 0.5,
                      lg: 0.5,
                      xl: 1,
                    },
                    textAlign: "center",
                    borderBottom: `2px solid ${themes.sidebarDivider}`,
                  }}
                >
                  <Mui.Typography sx={formHeader}>Create User</Mui.Typography>
                </Mui.Grid>
                <Mui.Grid
                  container
                  sx={{
                    padding: 1,
                  }}
                >
                  <Mui.Grid xs={12} sm={12} md={12} lg={6} xl={6}>
                    <Mui.Typography sx={formKeyText}>
                      User Name&nbsp;
                      <Mui.Tooltip
                        arrow
                        slotProps={tooltipStyleProps}
                        title={
                          "Enter the full name of the user. This will be used to identify the individual in the system."
                        }
                      >
                        <Info />
                      </Mui.Tooltip>
                    </Mui.Typography>
                  </Mui.Grid>
                  <Mui.Grid
                    xs={12}
                    sm={12}
                    md={12}
                    lg={5}
                    xl={5}
                    sx={formRightSideAlign}
                  >
                    <Mui.TextField
                      {...register("name", {
                        required: "Name is required",
                        maxLength: {
                          value: 30,
                          message: "Maximum length is 30 characters",
                        },
                        minLength: {
                          value: 3,
                          message: "Minimum length is 3 characters",
                        },
                        validate: {
                          noDigitsOrSpecial: (fieldValue) => {
                            if (!/[A-Za-z]/.test(fieldValue)) {
                              return "Name must contain at least one alphabet character.";
                            }
                            if (/^\d+$/.test(fieldValue)) {
                              return "Name should not contain only digits.";
                            }
                            if (!/^[A-Za-z0-9\s&-]+$/.test(fieldValue)) {
                              return "Name should only contain alphabets, spaces, -,numbers and & symbol.";
                            }
                            return true;
                          },
                          emailAvailable: async (fieldValue) => {
                            if (fieldValue !== "") {
                              const res = await customGetAPI(
                                `${domain}check_username/?username=${fieldValue}`
                              );
                              if (res?.ok) {
                                if (res?.responseDetails?.exists) {
                                  return "Name Already Exists";
                                } else {
                                  return true;
                                }
                              }
                            }
                          },
                        },
                      })}
                      name="name"
                      size="small"
                      error={errors.name ? true : false}
                      helperText={errors.name ? errors.name.message : null}
                      sx={{ background: "white", width: "100%" }}
                      placeholder="Enter the name"
                      onKeyPress={checkSpecialChar}
                      onBlur={() => trigger("name")}
                      // onPaste={handlePreventCopyPaste}
                    />
                  </Mui.Grid>
                </Mui.Grid>
                <Mui.Grid
                  container
                  sx={{
                    padding: 1,
                  }}
                >
                  <Mui.Grid xs={12} sm={12} md={12} lg={6} xl={6}>
                    <Mui.Typography sx={formKeyText}>
                      Phone Number&nbsp;
                      <Mui.Tooltip
                        arrow
                        slotProps={tooltipStyleProps}
                        title={
                          "Input the user’s active mobile number.This may be used for notifications, login authentication, or user verification."
                        }
                      >
                        <Info />
                      </Mui.Tooltip>
                    </Mui.Typography>
                  </Mui.Grid>
                  <Mui.Grid
                    xs={12}
                    sm={12}
                    md={12}
                    lg={5}
                    xl={5}
                    sx={formRightSideAlign}
                  >
                    <Mui.TextField
                      {...register("phoneno", {
                        required: "Phone number is required",
                        minLength: {
                          value: 10,
                          message: "Minimum length is 10 digits",
                        },
                        maxLength: {
                          value: 15,
                          message: "Maximum length is 15 digits",
                        },
                        validate: {
                          employeePhonenumberAvailable: async (fieldValue) => {
                            if (fieldValue !== "") {
                              const response = await fetch(
                                `${domain}unique/phone-number/?phone_no=${fieldValue}`,
                                {
                                  method: "GET",
                                  headers: {
                                    "Content-Type": "application/json",
                                    Authorization: `token ${token}`,
                                  },
                                }
                              );
                              const resp = await response.json();
                              if (resp.exists) {
                                return "Phone number already exists";
                              }
                            }
                          },
                        },
                      })}
                      size="small"
                      sx={{ background: "white" }}
                      onKeyPress={handleKeyPress}
                      placeholder="Enter the phone number"
                      error={errors.phoneno ? true : false}
                      helperText={
                        errors.phoneno ? errors.phoneno.message : null
                      }
                      // onPaste={handlePreventCopyPaste}
                      fullWidth
                    />
                  </Mui.Grid>
                </Mui.Grid>
                <Mui.Grid
                  container
                  sx={{
                    padding: 1,
                  }}
                >
                  <Mui.Grid xs={12} sm={12} md={12} lg={6} xl={6}>
                    <Mui.Typography sx={formKeyText}>
                      Company Name&nbsp;
                      <Mui.Tooltip
                        arrow
                        slotProps={tooltipStyleProps}
                        title={
                          "Select the company the user is associated with from the dropdown list.This links the user to relevant departments, permissions, and data access."
                        }
                      >
                        <Info />
                      </Mui.Tooltip>
                    </Mui.Typography>
                  </Mui.Grid>
                  <Mui.Grid
                    xs={12}
                    sm={12}
                    md={12}
                    lg={5}
                    xl={5}
                    sx={formRightSideAlign}
                  >
                    <CustomAutoComplete
                      dataList={companyDetails.map((obj) => ({
                        ...obj,
                        label: obj.companyname,
                      }))}
                      selectedValue={watch("company")}
                      rules={{
                        ...register("company", {
                          required: "Company name is required",
                        }),
                      }}
                      updateDataFunction={handleCompanyName}
                      placeHolder="Select the company name"
                      errorMessage={
                        errors?.company?.message ? errors?.company?.message : ""
                      }
                      textBoxError={errors.company ? true : false}
                    />
                  </Mui.Grid>
                </Mui.Grid>
                <Mui.Grid
                  container
                  sx={{
                    padding: 1,
                  }}
                >
                  <Mui.Grid xs={12} sm={12} md={12} lg={6} xl={6}>
                    <Mui.Typography sx={formKeyText}>
                      Email&nbsp;
                      <Mui.Tooltip
                        arrow
                        slotProps={tooltipStyleProps}
                        title={
                          "Provide a valid email address for the user.The email is essential for communication, system alerts, and possibly login credentials."
                        }
                      >
                        <Info />
                      </Mui.Tooltip>
                    </Mui.Typography>
                  </Mui.Grid>
                  <Mui.Grid
                    xs={12}
                    sm={12}
                    md={12}
                    lg={5}
                    xl={5}
                    sx={formRightSideAlign}
                  >
                    <Mui.TextField
                      {...register("email", {
                        required: false,
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: "Invalid e-mail address",
                        },
                        validate: {
                          emailAvailable: async (fieldValue) => {
                            if (fieldValue !== "") {
                              const res = await customGetAPI(
                                `${domain}email_verfication/?email=${fieldValue}`
                              );
                              if (res?.ok) {
                                if (res?.responseDetails?.exists) {
                                  return "Email Already Exists";
                                } else {
                                  return true;
                                }
                              }
                            }
                          },
                        },
                      })}
                      error={Boolean(errors.email)}
                      helperText={errors.email ? errors.email.message : null}
                      sx={{ background: "white" }}
                      placeholder="Enter the email"
                      size="small"
                      fullWidth
                    />
                  </Mui.Grid>
                </Mui.Grid>

                <Mui.Grid
                  container
                  sx={{
                    padding: 1,
                  }}
                >
                  <Mui.Grid xs={12} sm={12} md={12} lg={6} xl={6}>
                    <Mui.Typography sx={formKeyText}>
                      User Type&nbsp;
                      <Mui.Tooltip
                        arrow
                        slotProps={tooltipStyleProps}
                        title={"ToDo."}
                      >
                        <Info />
                      </Mui.Tooltip>
                    </Mui.Typography>
                  </Mui.Grid>
                  <Mui.Grid
                    xs={12}
                    sm={12}
                    md={12}
                    lg={5}
                    xl={5}
                    sx={{ mt: 1 }}
                  >
                    <Mui.RadioGroup
                      row
                      onChange={handleUserType}
                      aria-labelledby="demo-radio-buttons-group-label"
                      defaultValue={watch("user_type")}
                      name="radio-buttons-group"
                    >
                      <Mui.FormControlLabel
                        label="User"
                        value="User"
                        control={<Mui.Radio />}
                      />

                      <Mui.FormControlLabel
                        label="Super Admin"
                        value="Super Admin"
                        control={<Mui.Radio />}
                      />
                    </Mui.RadioGroup>
                  </Mui.Grid>
                </Mui.Grid>

                <Mui.Grid
                  container
                  sx={{
                    ...rowEndStyle,
                    paddingTop: { xs: 4, sm: 4, md: 2, lg: 2 },
                    paddingBottom: 2,
                    paddingRight: 1,
                  }}
                >
                  <CustomButton
                    actionName="Create"
                    typeName="submit"
                    disableAction={buttonLoader}
                  />
                </Mui.Grid>
              </Mui.Grid>
            </Mui.Grid>
          </form>
        </>
      )}
    </React.Fragment>
  );
};
